import { BaseImage } from "@components/BaseImage";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { Link } from "react-router-dom";
import type { PreservedSearchState } from "@hooks/usePreservedSearchQuery";
import { NewsMetaData } from "./NewsMetaData";
import { getDescriptionFromBody } from "@utils/getDescriptionFromBody";
import { SbBlokData } from "@storyblok/react";
import { Label } from "@components/Label";

interface NewsListItemProps {
  title: string;
  slug: string;
  imageUrl: string;
  imageAlt: string;
  date: string;
  cluster?: string;
  description?: string;
  body?: SbBlokData[];
  contentType?: string;
  breadcrumb?: string;
  linkState?: PreservedSearchState;
}

export const NewsListItem = ({
  title,
  slug,
  imageUrl,
  imageAlt,
  date,
  cluster,
  description,
  body,
  contentType,
  breadcrumb,
  linkState,
}: NewsListItemProps) => {
  // Use provided description or extract from body as fallback
  const displayDescription =
    description || (body ? getDescriptionFromBody(body) : null);

  const contentGap = displayDescription ? "gap-1 md:gap-3" : "gap-1";

  return (
    <div className="group relative flex items-center justify-between gap-3 bg-primary-50 p-4 md:gap-4 dark:bg-white/10">
      <div
        className={`flex flex-col ${contentGap} w-full max-w-prose text-balance`}
      >
        <div className="flex items-center justify-between gap-2">
          <Typography
            size="base"
            className="leading-[120%] text-pretty text-secondary-800 underline-offset-[4.68px] group-hover:underline lg:text-xl dark:text-white"
          >
            <Link to={slug} state={linkState}>
              {/* Make sure the whole card is clickable */}
              <span className="absolute inset-0"></span>
              {title}
            </Link>
          </Typography>
          {contentType && (
            <Label className="hidden shrink-0 self-start md:block">
              {contentType}
            </Label>
          )}
        </div>
        {breadcrumb && (
          <Typography
            size="xs"
            className="hidden font-normal text-tertiary-500 md:block dark:text-tertiary-400"
          >
            {breadcrumb}
          </Typography>
        )}
        {displayDescription && (
          <Typography
            className={`text-xs font-normal text-tertiary-600 max-md:hidden md:text-sm dark:text-tertiary-300 ${!imageUrl ? "line-clamp-4" : "line-clamp-2"}`}
          >
            {displayDescription}
          </Typography>
        )}
        {date && (
          <NewsMetaData
            date={date}
            dateFormat="long"
            cluster={cluster}
            className="*:font-medium"
          />
        )}
      </div>
      {imageUrl && (
        <BaseImage
          imageUrl={imageUrl}
          alt={imageAlt}
          className="h-22.5 w-32 object-cover lg:h-32 lg:w-44"
        />
      )}
    </div>
  );
};
